// The one action a Chapter row on the Book page offers, from its state and
// what it already has. Pure, so tests/client/chapter-action.test.ts walks
// every Chapter state without a DOM.
import type { ChapterSummary, JobSummary } from "@contract";

/** Each is a key under overview:chapterAction.*; a row with no action gets null. */
export const CHAPTER_ACTIONS = ["review", "listen", "failure", "watch"] as const;
export type ChapterAction = (typeof CHAPTER_ACTIONS)[number];

/** The failures card further down the Book page, which owns every parked job's retry. */
export const FAILURES_ANCHOR = "#failures";

/**
 * Review whenever there is a script to read, Listen once the Chapter has
 * audio, the failures card on a Chapter parked after its three attempts,
 * Watch while a job runs on it, and nothing for an excluded or still pending
 * Chapter. A rendered Chapter offers Listen: its script is reachable through
 * the row's title link (chapterReviewPath), so the row's own action is the one
 * that is new at that state.
 *
 * A parked Chapter points at the failures card rather than carrying a retry of
 * its own: the card holds the error, the attempt count, and the one button for
 * that job, and two buttons for one job on one screen send two POSTs.
 */
export function chapterAction(chapter: ChapterSummary): ChapterAction | null {
  if (chapter.excluded) return null;
  if (chapter.state === "failed") return "failure";
  if (chapter.state === "refining" || chapter.state === "rendering") return "watch";
  if (chapter.audio) return "listen";
  if (chapter.versionCount > 0) return "review";
  return null;
}

/**
 * The Chapter's own Review screen, or null while it has no Narration Script to
 * read. The row's title links here whatever the row's action is, so a Chapter
 * with audio still reaches its script.
 */
export function chapterReviewPath(bookId: string, chapter: ChapterSummary): string | null {
  if (chapter.excluded || chapter.versionCount === 0) return null;
  return `/books/${bookId}/review/${chapter.id}`;
}

/** Where the action goes; every action has a target, the parked one an anchor on this page. */
export function chapterActionPath(bookId: string, chapter: ChapterSummary): string | null {
  switch (chapterAction(chapter)) {
    case "review":
      return chapterReviewPath(bookId, chapter);
    case "listen":
      return `/api/books/${bookId}/chapters/${chapter.id}/audio?format=mp3`;
    case "failure":
      return FAILURES_ANCHOR;
    case "watch":
      return chapter.state === "rendering"
        ? `/books/${bookId}/render/progress`
        : `/books/${bookId}/review/${chapter.id}`;
    default:
      return null;
  }
}

/**
 * Fraction 0..1 of the work running on this Chapter, or null when nothing
 * reports a total. Refinement commits its progress onto the Chapter row and a
 * render commits it onto the job, so both are read: taking only the Chapter
 * would leave a rendering Chapter's bar empty for the whole render.
 *
 * `job` is the running job of this Chapter, which the caller looks up once for
 * the whole table (ChaptersCard builds one Map) rather than scanning the job
 * list per row.
 */
export function chapterProgress(
  chapter: ChapterSummary,
  job?: JobSummary | undefined,
): number | null {
  const progress = chapter.progress ?? (job?.status === "running" ? job.progress : null) ?? null;
  if (!progress || progress.total <= 0) return null;
  return Math.min(1, progress.done / progress.total);
}

/** One entry per Chapter that has a job in flight or parked, built once per render of the table. */
export function jobsByChapter(jobs: readonly JobSummary[]): ReadonlyMap<string, JobSummary> {
  const byChapter = new Map<string, JobSummary>();
  for (const job of jobs) {
    if (job.chapterId === null) continue;
    const held = byChapter.get(job.chapterId);
    // A running job is what a bar is drawn from; a parked one is what the
    // failures card retries. Prefer the one that is still moving.
    if (!held || (held.status !== "running" && job.status === "running")) {
      byChapter.set(job.chapterId, job);
    }
  }
  return byChapter;
}
